import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CommentService } from '../services/communityService';
import type { Comment } from '../types/community';

const CommentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin';

  const [comment, setComment] = useState<Comment | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin || !id) return;

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await CommentService.getCommentById(id);

        if (!isMounted) return;
        setComment(res);
        document.title = 'Comment Details';
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load comment');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAdmin, id]);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateValue = (value: unknown) => {
    if (!value) return '—';
    if (typeof value === 'string') return formatDate(value);
    if (typeof value === 'object' && value && '$date' in (value as any) && typeof (value as any).$date === 'string') {
      return formatDate((value as any).$date);
    }
    return '—';
  };

  const getUserLabel = (comment: Comment): string => {
    const v: any = comment.userId ?? comment.user;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const name = typeof v.name === 'string' ? v.name.trim() : '';
      const email = typeof v.email === 'string' ? v.email.trim() : '';
      if (name && email) return `${name} (${email})`;
      if (name) return name;
      if (email) return email;
    }
    return '—';
  };

  const getCommunityLabel = (comment: Comment): string => {
    const v: any = comment.communityId ?? comment.community;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const title = typeof v.title === 'string' ? v.title : '';
      const id = typeof v._id === 'string' ? v._id : '';
      return title || id || '—';
    }
    return '—';
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500 mb-4">You need admin privileges to access this page.</p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading comment details...</p>
      </div>
    );
  }

  if (error || !comment) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
        <p className="text-gray-500 mb-4">{error || 'Comment not found'}</p>
        <button
          onClick={() => navigate('/comments')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Comments
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comment Details</h1>
        </div>
        <button
          onClick={() => navigate('/comments')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back to Comments
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 sm:px-8">
          <div className="space-y-6">
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Description</label>
              <p className="mt-2 text-sm text-gray-900 whitespace-pre-wrap">{comment.description || '—'}</p>
            </div>

            {/* Community Post */}
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Community Post</label>
              <div className="mt-2">
                <button
                  onClick={() => {
                    const communityId = typeof comment.communityId === 'string' ? comment.communityId : (comment.community as any)?._id;
                    if (communityId) {
                      navigate(`/community/${encodeURIComponent(communityId)}`);
                    }
                  }}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  {getCommunityLabel(comment)}
                </button>
              </div>
            </div>

            {/* User */}
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">User</label>
              <p className="mt-2 text-sm text-gray-900">{getUserLabel(comment)}</p>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Status</label>
              <div className="mt-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  comment.status === 'approved' ? 'bg-green-100 text-green-800' :
                  comment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {comment.status || 'pending'}
                </span>
              </div>
            </div>

            {/* Created Date */}
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Created</label>
              <p className="mt-2 text-sm text-gray-900">{formatDateValue(comment.createdAt)}</p>
            </div>

            {/* Updated Date */}
            {comment.updatedAt && (
              <div>
                <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Updated</label>
                <p className="mt-2 text-sm text-gray-900">{formatDateValue(comment.updatedAt)}</p>
              </div>
            )}

            {/* Comment ID */}
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">Comment ID</label>
              <p className="mt-2 text-sm text-gray-900 break-all font-mono">{comment._id}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentDetail;
